import React, { FunctionComponent, useEffect, useCallback, useState, useRef } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import debounce from 'lodash/debounce'
import { INLINE_MESSAGE_VARIANTS, InlineMessageProps } from 'transfix-ui/components/InlineMessage'
import { makeStyles, Typography, Hidden, Container } from 'transfix-ui/core'
import Box from 'transfix-ui/core/Box'
import Grid from 'transfix-ui/core/Grid'
import { brandColors } from 'transfix-ui/theme/colors'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useChatWidgetContext } from 'utils/context/ChatWidgetContext'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { SectionAttachments } from 'apps/fleet-planner/components/create-load/SectionAttachments'
import { SectionBasics } from 'apps/fleet-planner/components/create-load/SectionBasics'
import { SectionLoadDetails } from 'apps/fleet-planner/components/create-load/SectionLoadDetails'
import { SectionNotes } from 'apps/fleet-planner/components/create-load/SectionNotes'
import { SectionStops } from 'apps/fleet-planner/components/create-load/SectionStops'
import { FullPageOverlay } from 'apps/fleet-planner/components/modals/full-page-overlay/FullPageOverlay'
import { SidebarSummary } from 'apps/fleet-planner/components/sidebar-summary/SidebarSummary'
import { getStepsArray } from 'apps/fleet-planner/components/sidebar-summary/stepUtils'
import { useCreateLoadFormContext } from 'apps/fleet-planner/context'
import { useWatchLoad } from 'apps/fleet-planner/hooks'
import {
  useMembershipValidation,
  IAssociatedMessage,
} from 'apps/fleet-planner/hooks/useMembershipValidation'
import { CreateLoadSchemaType } from 'apps/fleet-planner/schema'
import { getRecentFacilities } from 'apps/fleet-planner/utils/actions/facilities'
import { createLoad } from 'apps/fleet-planner/utils/actions/loads'
import { getCreateLoadRequest } from 'apps/fleet-planner/utils/getCreateLoadRequest'
import { isElementIdInViewport } from 'apps/fleet-planner/utils/isElementIdInView'
import { findCarrierById } from 'apps/fleet-planner/utils/motorCarrierHelpers'
import { selectRecentFacilities, selectCarriersList } from 'apps/fleet-planner/utils/selectors'
import { Button, Toast, useToastState } from 'transfix-ui'
import { usePrevious } from 'utils/usePrevious'
import { ConfirmAssociationModal } from './ConfirmAssociationsModal'

const useCreateOverlayStyles = makeStyles(theme => ({
  stickySidebarContainer: {
    position: 'relative',
    paddingTop: theme.spacing(1),
    top: 0,
    /**
     * Ensures that the screen is large enough before
     * enabling "sticky" positioning
     */
    '@media (min-height:600px)': {
      position: 'sticky',
    },
  },
  label: {
    color: brandColors.coolGray5,
  },
}))

interface CreateLoadOverlayProps {
  showModal: boolean
  error?: string
  saving?: boolean
}

/**
 * Composes message for <InlineMessage /> component in <ModalBase />
 * @param {string} [error]
 * @param {string} validationErrorMessage
 */
export const getInlineMessageProps = (
  error?: string,
  validationErrorMessage: string = ''
): InlineMessageProps | undefined => {
  const message = error || validationErrorMessage
  return message
    ? {
        message,
        dataTest: 'modal-add-load-error',
        type: INLINE_MESSAGE_VARIANTS.ALERT,
      }
    : undefined
}

/**
 * Full screen overlay used for creating a load
 */
export const CreateLoadOverlay: FunctionComponent<CreateLoadOverlayProps> = ({
  error,
  saving,
  showModal,
}) => {
  const carriers = useSelector(selectCarriersList)
  const { getFlag } = useFeatureFlagContext()
  const { isAdminForCarrier } = useMembershipValidation()
  const { enabled: isFpCarrierPortalEnabled } = getFlag('fpCarrierPortal')
  const { control, getValues, handleSubmit, setValue } = useCreateLoadFormContext()
  const { isMobile } = useBreakpointContext()
  const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()
  const { getUnassociatedItemsFromLoad } = useMembershipValidation()
  const history = useHistory()
  const dispatch = useDispatch()
  const { boot, shutdown, update } = useChatWidgetContext()

  const { stops = [] } = useWatchLoad({
    control,
  })

  /**
   * Forces Intercom to refresh so that product tour can
   * transition properly
   */
  useEffect(() => {
    update()
  }, [update])

  /**
   * Close modal and go back to page.
   * Resets validation and sets default load state too
   * Remounts the chat widget after closing the modal.
   */
  const closeModalHandler = useCallback(() => {
    history.push(history.location.pathname.replace('/create-load', ''))
    boot()
  }, [boot, history])

  // If it's no longer saving, close the modal
  const prevSaving = usePrevious(saving)
  useEffect(() => {
    if (showModal && prevSaving && !saving && !error) closeModalHandler()
  }, [saving, showModal, prevSaving, error, closeModalHandler])

  const inlineMessageProps = getInlineMessageProps(error)

  // opens the Toast message and scrolls to the attachment table on attachment success
  const attachmentSuccessHandler = (message: string) => {
    openToast({ toastType: 'default', toastMessage: message })
    const attachments = document.getElementById('add-load-attachments')
    if (attachments) attachments.scrollIntoView({ behavior: 'smooth' })
  }

  // Active step shown in <VerticalLinearStepper />
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0)
  const steps = getStepsArray(stops, isFpCarrierPortalEnabled)

  /**
   * Handles onScroll event by setting a local state
   * with an element Id of a matching element in the
   * main section
   */
  const scrollHandler = debounce(() => {
    if (!isMobile) {
      // Returns array of booleans indicating which elements are in view
      const stepsInViewArray: boolean[] = steps.map(({ classId }) => isElementIdInViewport(classId))
      const stepIndex = stepsInViewArray.findIndex(step => !!step)
      /**
       * Update the selected section in <SidebarSummary />
       */
      if (stepsInViewArray[stepIndex]) setActiveStepIndex(stepIndex)
    }
  }, 50)

  const openHandler = () => {
    // hide the chat widget when the page loads
    shutdown()
  }

  // store an array of messages if a driver/dispatcher
  // is not associated with selected carrier
  const [associatedMessages, setAssociatedMessages] = useState<IAssociatedMessage[]>([])

  // temporarily store a request when we're asking the user for confirmation
  const [pendingRequest, setPendingRequest] = useState<CreateLoadSchemaType | undefined>()

  /**
   * Saves load via API post call to /shipment
   * @param {CreateLoadSchemaType} data - The validated form data
   */
  const submitHandler = (data: CreateLoadSchemaType) => {
    const newLoad = getCreateLoadRequest(data)

    // Only execute this `if` block when `isFpCarrierPortalEnabled` is true
    const unassociatedItems = getUnassociatedItemsFromLoad(data)
    const isAdmin = isAdminForCarrier(newLoad.motorCarrierId)
    const hasWarnings = unassociatedItems.length

    // only show if admin user, otherwise errors are
    // displayed before this inline in form
    if (isFpCarrierPortalEnabled && isAdmin && hasWarnings) {
      setPendingRequest(newLoad)
      setAssociatedMessages(unassociatedItems)
      return
    }

    dispatch(createLoad(newLoad as any))
  }

  /**
   * `pendingRequest` and `associatedMessages` will clear when the dispatch succeeds
   */
  const confirmHandler = () => {
    dispatch(createLoad(pendingRequest as any))
  }

  const cancelModalHandler = useCallback(() => {
    // reset this data
    setPendingRequest(undefined)
    setAssociatedMessages([])
  }, [])

  /**
   * For displaying errors when
   */
  const submitErrorHandler = (errors: FieldErrors<CreateLoadSchemaType>) => {
    // eslint-disable-next-line no-console
    console.log('submitErrorHandler', {
      errors,
      values: getValues(),
    })
  }

  const formSubmitHander = handleSubmit(submitHandler, submitErrorHandler)
  const classes = useCreateOverlayStyles()

  /**
   * This is to make sure that RHF re-renders and
   * includes the programmatically generated stops in
   * the sidebar summary
   */
  const hasTriggeredInitialValues = useRef(false)
  useEffect(() => {
    if (stops.length && !hasTriggeredInitialValues.current) {
      setValue(`stops[0].startAt`, '')
      hasTriggeredInitialValues.current = true
    }
  }, [setValue, stops])

  /**
   * If facilities are empty, fetch recent facilities
   * to populate Pickup and Dropoff location autosuggests.
   */
  const facilities = useSelector(selectRecentFacilities)
  const hasFetchedFacilities = useRef(false)
  useEffect(() => {
    if (!facilities?.length && !hasFetchedFacilities.current) {
      dispatch(getRecentFacilities({ pageSize: 1000 }))
      hasFetchedFacilities.current = true
    }
  }, [dispatch, facilities?.length])

  // Used for displaying carrier name in modal
  const motorCarrier = pendingRequest?.motorCarrierId
    ? findCarrierById(carriers, pendingRequest?.motorCarrierId)
    : undefined
  const { name: motorCarrierName } = motorCarrier || {}

  return (
    <FullPageOverlay
      className='create-load-overlay'
      onSubmit={formSubmitHander}
      title='Add New Load'
      isOpen={showModal}
      onOpen={openHandler}
      onClose={closeModalHandler}
      inlineMessageProps={inlineMessageProps}
      onScroll={scrollHandler}
      footer={
        <>
          <Button
            color='tertiary'
            className='mt0'
            size='large'
            fullWidth={isMobile}
            onClick={closeModalHandler}
            data-test='modal-add-load-cancel'>
            Cancel
          </Button>
          <Button
            color='primary'
            className='mt0'
            size='large'
            type='submit'
            onClick={formSubmitHander}
            disabled={!!saving}
            fullWidth={isMobile}
            data-test='modal-add-load-save'>
            {saving ? 'Saving...' : 'Add Load'}
          </Button>
        </>
      }>
      <Container maxWidth='xl' disableGutters>
        <Box>
          <Grid container spacing={isMobile ? 0 : 3}>
            <Grid item xs={12} lg={1}>
              <span />
            </Grid>
            <Hidden mdDown>
              <Grid item xs={12} md={4} lg={3}>
                <Box className={classes.stickySidebarContainer}>
                  <SidebarSummary
                    activeStep={activeStepIndex}
                    onSetActiveStep={setActiveStepIndex}
                  />
                </Box>
              </Grid>
            </Hidden>
            <Grid item xs={12} md={8} lg={7}>
              <Box pt={isMobile ? 0 : 1} maxWidth={657}>
                <SectionBasics />
                <SectionStops />
                <SectionLoadDetails />
                <SectionNotes />
                <SectionAttachments onSuccess={attachmentSuccessHandler} />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>
      <Toast variation={toastType} open={toastOpen} onClose={closeToast} message={toastMessage} />
      {/**
       * Prompts the user to confirm if they want to create associations
       * to motor carriers for dispatchers/drivers that were added to the load
       */}
      <ConfirmAssociationModal
        isLoading={!!saving}
        open={!!associatedMessages.length}
        onConfirm={confirmHandler}
        onCancel={cancelModalHandler}>
        <Box mb={-2}>
          <Box mb={2}>
            <Typography variant='subtitle2' className={classes.label}>
              Carrier
            </Typography>
            <Typography variant='body2'>{motorCarrierName}</Typography>
          </Box>
          {associatedMessages.map(item => {
            const { name, value } = item || {}
            return (
              <Box key={name} mb={2}>
                <Typography variant='subtitle2' className={classes.label}>
                  {name} (doesn&apos;t have access)
                </Typography>
                <Typography variant='body2'>{value}</Typography>
              </Box>
            )
          })}
        </Box>
      </ConfirmAssociationModal>
    </FullPageOverlay>
  )
}
